/* eslint-disable @typescript-eslint/no-explicit-any */
import Icon from "@vfuk/core-icon";
import Paragraph from "@vfuk/core-paragraph";
import Spacing from "@vfuk/core-spacing";
import { forwardRef, ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  components,
  ControlProps,
  IndicatorProps,
  StylesConfig,
  OptionProps,
} from "react-select";
import { SelectComponentsProps } from "react-select/src/Select";
import FloatingLabelBase from "../FloatingLabelBase/FloatingLabelBase";
import Image from "@vfuk/core-image";
import classes from "./SelectInput.module.scss";
import "./SelectInput.scss";
import { Subtext } from "../Subtext";
import { Heading } from "@vfuk/core-heading";
import styled from "styled-components";

type IsMulti = false;

export type SelectOption = {
  value: any;
  label: string | ReactNode;
  disabled?: boolean;
  [key: string]: any;
};

const selectStyle: StylesConfig<SelectOption, IsMulti> = {
  valueContainer: (provided) => ({
    ...provided,
    height: "100%",
    padding: "0 1rem",
  }),
  control: (provided, state) => ({
    ...provided,
    borderColor: state.selectProps.state === "error" ? "#bd0000" : "#7e7e7e",
    borderWidth: state.selectProps.state === "error" ? "2px" : "1px",
    boxSizing: "border-box",
    height: "3.75rem",
    "&:hover": {
      borderColor: undefined,
    },
    backgroundColor: "white",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
  }),
  placeholder: (provider, state) => ({
    ...provider,
    display: state.isFocused || state.hasValue ? "inline-block" : "none",
  }),
  indicatorSeparator: () => ({}),
  dropdownIndicator: (provided) => ({
    ...provided,
    paddingRight: "1rem",
  }),
  menuList: (provided) => ({
    ...provided,
    'div[class*="option"]:not(:last-child)': {
      borderBottom: "1px solid #EBEBEB",
    },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    color: "black",
    backgroundColor: state.isFocused ? "#EBEBEB" : undefined,
    "&:hover": {
      backgroundColor: "#EBEBEB",
    },
    "&:active": {
      backgroundColor: "#EBEBEB",
    },
  }),
};

const InlineLabelContainer = styled.div`
  padding-left: 16px;
  line-height: 26px;
`;

const Control = (props: ControlProps<SelectOption, IsMulti>) => (
  <>
    <FloatingLabelBase
      spaceForError={props.selectProps.spaceForError && !props.isMulti}
      fieldHeightSmall={props.selectProps.fieldHeightSmall}
      forceFloat={
        props.getValue().length > 0 && props.selectProps.placeholder !== ""
      }
      label={props.selectProps.label}
      id={props.selectProps.inputId || ""}
      state={props.selectProps.state}
      error={props.selectProps.error}
      disabled={props.selectProps.isDisabled}
      readonly={props.selectProps.readonly}
    >
      <components.Control {...props}>
        {props.selectProps.inlineLabel && (
          <InlineLabelContainer>
            <Heading size={1} weight={2} noMargin>
              {props.selectProps.inlineLabel}
            </Heading>
          </InlineLabelContainer>
        )}
        {props.children}
      </components.Control>
    </FloatingLabelBase>
    {props.selectProps.subtext && <Subtext text={props.selectProps.subtext} />}
  </>
);

const DropdownIndicator = (props: IndicatorProps<SelectOption, IsMulti>) => (
  <components.DropdownIndicator {...props}>
    <Icon name="chevron-down" size={1} />
  </components.DropdownIndicator>
);

const ClearIndicator = (props: IndicatorProps<SelectOption, IsMulti>) => (
  <components.ClearIndicator {...props}>
    <Icon name="close" size={1} />
  </components.ClearIndicator>
);

const NoOptionsMessage = (props: IndicatorProps<SelectOption, IsMulti>) => {
  const { t } = useTranslation();
  return (
    <>
      {props.selectProps.fetchError ? (
        <Spacing spacingLevel={{ top: 8, bottom: 8 }}>
          <div className={classes.FetchError}>
            <Spacing spacingLevel={{ right: 2 }}>
              <Icon name="warning" size={1} />
            </Spacing>
            <Paragraph>{t("core:common:errors.dropdown")}</Paragraph>
          </div>
        </Spacing>
      ) : (
        <components.NoOptionsMessage {...props} />
      )}
    </>
  );
};

// Custom Select option that allows for icons
const Option = (props: OptionProps<SelectOption, IsMulti>) => (
  <components.Option {...props}>
    {props.data.icon && (
      <Spacing spacingLevel={{ right: 3 }}>
        <Image sm={{ src: props.data.icon }} alt={props.data.label} />
      </Spacing>
    )}
    {props.data.label || props.label}
  </components.Option>
);

type SelectInputProps = {
  id: string;
  label: string;
  subtext?: string;
  spaceForError?: boolean;
  fieldHeightSmall?: boolean;
  fetchError?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  noOptionsMessage?: string;
  readonly?: boolean;
  directValueMatch?: boolean;
  inlineLabel?: string;
} & SelectComponentsProps;

const SelectInput = forwardRef<Select, SelectInputProps>(
  (
    {
      spaceForError = true,
      isLoading,
      noOptionsMessage,
      ...props
    }: SelectInputProps,
    ref
  ): JSX.Element => {
    const value = useMemo(() => {
      if (props.directValueMatch)
        return props.options.find(
          ({ value: optionValue }: { value: string }) =>
            optionValue === props.value
        );

      return props.value ?? null;
    }, [props.value, props.directValueMatch, props.options]);

    const onChange = useCallback(
      (option: SelectOption | null) => {
        if (!option) {
          props.onChange(undefined);
        } else if (props.directValueMatch) {
          props.onChange(option.value);
        } else {
          props.onChange(option);
        }
      },
      [props.directValueMatch, props.onChange]
    );

    return (
      <Select
        inputId={`${props.id}Input`}
        instanceId={`${props.id}Input`}
        classNamePrefix={props.id}
        isClearable={false}
        menuPortalTarget={document.body}
        spaceForError={spaceForError}
        isLoading={isLoading}
        // This is genuinely the type they've chosen for this prop... why...?
        noOptionsMessage={() => noOptionsMessage || "No options"}
        {...props}
        components={{
          Control,
          ClearIndicator,
          DropdownIndicator,
          NoOptionsMessage,
          Option,
          ...props.components,
        }}
        styles={{ ...selectStyle, ...props.styles }}
        ref={ref}
        // This allows us to set the select option as undefined to reset it instead of null
        value={value}
        onChange={onChange}
      />
    );
  }
);

SelectInput.displayName = "SelectInput";

export default SelectInput;
